import * as PIXI from "pixi.js";
import { Spine } from "@pixi/spine-pixi";

// Create a SymbolPool class to manage the pool of symbols
class SymbolPool {
    private pool: Symbol[] = [];

    constructor(initialPoolSize: number = 10) {
        for (let i = 0; i < initialPoolSize; i++) {
            this.pool.push(new Symbol('1'));
        }
    }

    public acquire(): Symbol {
        if (this.pool.length > 0) {
            const symbol = this.pool.pop();
            if (symbol) {
                symbol.alpha = 1;
                symbol.position.set(0, 0);
                symbol.scale.set(1);
                symbol.visible = true;
            }
            return symbol!;
        } else {
            return new Symbol('1');
        }
    }

    public release(symbol: Symbol) {
        symbol.parent?.removeChild(symbol);
        this.pool.push(symbol);
    }
}
export class Symbol extends PIXI.Container {
    private type: string | undefined;
    private symbol: Spine;
    private multText: PIXI.BitmapText;
    private multiplier: number = 0;

    public originalPosition!: PIXI.Point;
    public originalParent!: PIXI.Container;

    public static SYMBOL_WIDTH: number = 125; // Default width of the Symbol.
    public static SYMBOL_HEIGHT: number = 115; // Default height of the Symbol.

    private static _pool: SymbolPool;

    public static get pool(): SymbolPool {
        if (!Symbol._pool) {
            throw new Error('Symbol pool is not initialized. Call Symbol.createPool() first.');
        }
        return Symbol._pool;
    }

    public static createPool(initialPoolSize: number = 10) {
        Symbol._pool = new SymbolPool(initialPoolSize);
    }

    constructor(type: string) {
        super();
        this.symbol = Spine.from({
            skeleton: 'h1Json',
            atlas: 'h1Atlas',
        });

        this.addChild(this.symbol);
        this.multText = new PIXI.BitmapText({
            text: '',
            style: {
                fontSize: 40,
                fontFamily: "multiplierFont",
            }
        });
        this.multText.anchor.set(0.5);
        this.multText.zIndex = 1;
        this.addChild(this.multText);
        this.setType(type.toString());
        this.symbol.state.addListener({
            event: function(entry, event) { },
            complete: (entry) => {
                if (entry.animation) {
                    this.emit(entry.animation.name);
                }
            },
            start: () => {
            },
            end: function(entry) { },
            dispose: function(entry) { },
        })

    }

    getType() {
        return this.type;
    }

    getMultiplier() {
        return this.multiplier;
    }

    setType(type: string) {
        this.removeChild(this.symbol);
        this.symbol.destroy();
        this.type = type;
        let symbolName: string = '';
        this.zIndex = 0;
        this.multText.visible = false;
        let multSkin = 'x1';
        switch (this.type) {
            case '1':
                symbolName = 'h1';
                break;
            case '2':
                symbolName = 'h2';
                break;
            case '3':
                symbolName = 'h3';
                break;
            case '4':
                symbolName = 'h4';
                break;
            case '5':
                symbolName = 'l1';
                break;
            case '6':
                symbolName = 'l2';
                break;
            case '7':
                symbolName = 'l3';
                break;
            case '8':
                symbolName = 'l4';
                break;
            case '10':
                symbolName = 'bonus';
                this.zIndex = 10;
                break;
            default:
                this.type = '100';
                this.multiplier = parseInt(type) - 100;
                this.multText.visible = true;
                this.multText.text = `x${this.multiplier.toString()}`;
                if (this.multiplier < 5) {
                    multSkin = 'x3';
                } else if (this.multiplier < 10) {
                    multSkin = 'x25';
                } else if (this.multiplier < 50) {
                    multSkin = 'x50';
                } else {
                    multSkin = 'x100';
                }

                symbolName = 'multiplier';
                this.zIndex = 11;
                break;
        }

        if (symbolName[0] === 'l') {
            this.symbol = Spine.from({
                skeleton: 'lowJson',
                atlas: 'lowAtlas',
            });
            this.symbol.skeleton.setSkinByName(symbolName);
        } else {
            this.symbol = Spine.from({
                skeleton: `${symbolName}Json`,
                atlas: `${symbolName}Atlas`,
            });
        }
        if (this.type === '100') {
            this.symbol.skeleton.setSkinByName(multSkin);
        }
        if (!this.symbol) {
            console.error('Skeleton not found ' + symbolName);
            return;
        }
        this.symbol.skeleton.setSlotsToSetupPose();
        this.symbol.scale.set(0.1);
        this.symbol.y = 0;
        this.symbol.angle = 0;
        this.symbol.name = symbolName;

        this.addChild(this.symbol);
        this.idle();
    }

    setMultiplier(multiplier: number) {
        if (this.type !== '10') {
            console.error('Only wild symbols can have multipliers');
            return;
        }
        this.multiplier = multiplier;
        if (multiplier === 1) {
            this.symbol.skeleton.setSkinByName(`w`);
        } else {
            this.symbol.skeleton.setSkinByName(`x${multiplier}`);
        }

        this.symbol.skeleton.setSlotsToSetupPose();
        // this.symbol.state.setAnimation(0, 'm_idle_on', true);
        this.idle();
    }

    public async win(quickStop?: boolean): Promise<void> {
        return new Promise((resolve) => {
            try {
                this.symbol.state.setAnimation(0, 'match', false);
            } catch (error) {
                console.error(error);
                console.log(this.symbol.name);
                console.log(this.symbol.skeleton.data.animations);
            }
            if (this.type !== '100' && this.type !== '10') {
                this.symbol.state.addAnimation(0, 'remove', false);
                setTimeout(() => {
                    resolve();
                }, 2000);
            } else {
                this.multText.visible = false;

                setTimeout(() => {
                    resolve();
                }, 1500);
            }
        });
    }

    public land() {
        try {
            this.symbol.state.setAnimation(0, 'land', false);
            this.symbol.state.addAnimation(0, 'idle', true);
        } catch (error) {
            console.error(error);
            console.log(this.symbol.name);
            console.log(this.symbol.skeleton.data.animations);
        }
    }

    public idle() {
        let animationName = 'idle';
        if (this.type === '100') {
            animationName = 'idle_off';
        }
        // Get the animation duration (in frames or time)
        const animation = this.symbol.skeleton.data.findAnimation(animationName);
        if (animation) {
            const duration = animation.duration;

            // Randomly pick a time between 0 and the animation duration
            const randomTime = Math.random() * duration;

            // Set the animation to play from the random time
            this.symbol.state.setAnimation(0, animationName, true).timeScale = 0.5; // loop is set to true

            this.symbol.state.update(randomTime);
        }
    }
}
